// Full Updated File: pages/Login.jsx
// Professional redesign inspired by checkout and order pages
// Premium design with improved structure, spacing, and visual hierarchy

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { Mail, Lock, LogIn, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import './css/LoginStyles.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useUser();

  useEffect(() => {
    // Check for success message from registration
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    try {
      const response = await authAPI.login({ email, password });
      localStorage.setItem('token', response.data.token);
      updateUser(response.data.user);
      navigate(response.data.user.isAdmin ? '/admin' : '/');
    } catch (err) {
      setError(err.response?.data?.message || 'Echec de connexion. Veuillez verifier vos identifiants.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      {/* Header */}
      <div className="login-header-bar">
        <button onClick={() => navigate('/')} className="login-back-button">
          <ArrowLeft size={20} />
          <span>Retour a l'accueil</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="login-content">
        <div className="login-hero-section">
          <div className="login-hero-icon-wrapper">
            <LogIn size={48} className="login-hero-icon" />
          </div>
          <h1 className="login-hero-title">Bon retour</h1>
          <p className="login-hero-subtitle">
            Connectez-vous pour continuer vos achats
          </p>
        </div>

        <div className="login-form-wrapper">
          <div className="login-card">
            {/* Success Message */}
            {successMessage && (
              <div className="login-success-banner" role="alert">
                <CheckCircle size={18} className="login-success-icon" />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="login-error-banner" role="alert">
                <AlertCircle size={18} className="login-error-icon" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              {/* Email Input */}
              <div className="login-form-group">
                <label className="login-label">Adresse email</label>
                <div className="login-input-wrapper">
                  <Mail size={18} className="login-input-icon" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="login-input"
                    placeholder="john.doe@example.com"
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="login-form-group">
                <label className="login-label">Mot de passe</label>
                <div className="login-input-wrapper">
                  <Lock size={18} className="login-input-icon" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="login-input"
                    placeholder="Entrez votre mot de passe"
                    disabled={isSubmitting}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="login-submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="login-spinner"></div>
                    <span>Connexion...</span>
                  </>
                ) : (
                  <>
                    <LogIn size={20} />
                    <span>Se connecter</span>
                  </>
                )}
              </button>

              {/* Register Link */}
              <div className="login-footer">
                <p className="login-footer-text">
                  Pas de compte ?{' '}
                  <Link to="/register" className="login-footer-link">
                    Creez-en un
                  </Link>
                </p>
              </div>
            </form>
          </div>

          {/* Info Box */}
          <div className="login-info-box">
            <CheckCircle size={20} className="login-info-icon" />
            <div className="login-info-content">
              <h3 className="login-info-title">Connexion securisee</h3>
              <p className="login-info-text">
                Vos identifiants sont chiffres et securises. Nous ne partageons jamais vos informations personnelles.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
